import React from 'react'

import {
  OverlayTrigger,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonProps,
  Tooltip,
} from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'

type ModeSelectorProps<T extends string> = {
  modes: T[]
  selectedMode: T
  setMode: (mode: T) => void
  formatMode?: (mode: T) => React.ReactNode
  buttonProps?: Partial<ToggleButtonProps>
  size?: 'sm'
}

export default function ModeSelector<T extends string>(
  props: ModeSelectorProps<T>,
): React.ReactElement {
  const { selectedMode, modes, setMode, formatMode, buttonProps, size } = props

  return (
    <ToggleButtonGroup
      type="radio"
      name="mode"
      onChange={setMode}
      value={selectedMode}
      size={size}
    >
      {modes.map(mode =>
        formatMode ? (
          <ToggleButton
            variant="light"
            key={mode}
            value={mode}
            {...buttonProps}
            style={{ padding: 0, ...buttonProps?.style }}
          >
            <OverlayTrigger
              key={mode}
              overlay={<Tooltip id={`${mode}-tooltip`}>{mode}</Tooltip>}
              placement="bottom"
            >
              <span
                style={{
                  display: 'inline-block',
                  padding: '0.375rem 0.75rem',
                  height: '100%',
                }}
              >
                {formatMode(mode)}
              </span>
            </OverlayTrigger>
          </ToggleButton>
        ) : (
          <ToggleButton
            key={mode}
            variant="light"
            value={mode}
            {...buttonProps}
          >
            {mode}
          </ToggleButton>
        ),
      )}
    </ToggleButtonGroup>
  )
}

type Control = ReturnType<typeof useForm>['control']
type UncontrolledProps<T extends string> = {
  modes: T[]
  formatMode?: (mode: T) => React.ReactNode
  name: string
  control: Control
  defaultValue: T
}

function Uncontrolled<T extends string>({
  modes,
  formatMode = mode => mode,
  name,
  control,
  defaultValue,
}: UncontrolledProps<T>): React.ReactElement {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ ref, onChange, onBlur, name, value }) => (
        <ToggleButtonGroup
          type="radio"
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          value={value}
        >
          {modes.map(mode => (
            <ToggleButton
              type="radio"
              variant="light"
              key={mode}
              name={name}
              value={mode}
              inputRef={ref}
            >
              {formatMode(mode)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    />
  )
}

ModeSelector.Uncontrolled = Uncontrolled
